import { memo, useCallback } from 'react';

import { ArrowLeftIcon, ArrowRightIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { cn } from '@air/utils-tailwind';

import { PDFPageControls } from '~/components/AssetModalV2/Visualizer/PDFVisualizer/PDFControls/components/PDFPageControls';
import { isMobileAgent } from '~/utils/PlatformHelpers';

export type PDFPageControlsData = {
  currentPage: number;
  totalPages: number;
  goToPage: (pageNumber: number) => void;
};

export type PreviousAndNextAssetModalArrowsProps = {
  onNextClick?: () => void;
  onPreviousClick?: () => void;
  className?: string;
  pdfPageControls?: PDFPageControlsData | null;
};

export const PreviousAndNextAssetModalArrows = memo(
  ({ onNextClick, onPreviousClick, className, pdfPageControls }: PreviousAndNextAssetModalArrowsProps) => {
    const hasPrevious = !!onPreviousClick;
    const hasNext = !!onNextClick;

    const onFocusChange = useCallback(() => {}, []);

    return (
      <div
        className={cn(
          'pointer-events-none flex w-full shrink-0 items-center justify-between bg-black px-4 py-2',
          className,
        )}
      >
        <IconButton
          appearance="ghost"
          color="gray"
          disabled={!hasPrevious}
          size={isMobileAgent ? 'medium' : 'large'}
          onClick={onPreviousClick}
          className="pointer-events-auto disabled:bg-transparent ads:disabled:text-icon-subtle disabled:text-gray-8"
          label="Previous asset"
          data-testid={hasPrevious ? 'ASSET_MODAL_PREVIOUS' : 'ASSET_MODAL_PREVIOUS_DISABLED'}
        >
          <ArrowLeftIcon size={isMobileAgent ? 'sm' : 'lg'} />
        </IconButton>

        {pdfPageControls && pdfPageControls.totalPages > 0 && (
          <div className="pointer-events-auto rounded-8 ads:bg-base bg-gray-1 px-2 py-1">
            <PDFPageControls
              currentPage={pdfPageControls.currentPage}
              totalPages={pdfPageControls.totalPages}
              goToPage={pdfPageControls.goToPage}
              onFocusChange={onFocusChange}
            />
          </div>
        )}

        <IconButton
          appearance="ghost"
          color="gray"
          disabled={!hasNext}
          size={isMobileAgent ? 'medium' : 'large'}
          onClick={onNextClick}
          data-testid={hasNext ? 'ASSET_MODAL_NEXT' : 'ASSET_MODAL_NEXT_DISABLED'}
          className="pointer-events-auto disabled:bg-transparent ads:disabled:text-icon-subtle disabled:text-gray-8"
          label="Next asset"
        >
          <ArrowRightIcon size={isMobileAgent ? 'sm' : 'lg'} />
        </IconButton>
      </div>
    );
  },
);

PreviousAndNextAssetModalArrows.displayName = 'PreviousAndNextAssetModalArrows';
